import { createStore } from 'vuex'
import api, { saveCsrfToken, API_URL } from '@/utils/api'
import {
  clearHostCsrfToken,
  clearHostSessionKey,
  getHostSessionKey,
  setHostSessionKey,
} from '@/utils/hostScopedStorage'
import { buildWaMeUrl, resolvePublicWhatsappHref } from '@/utils/whatsappLink'
import { clearSetupPromptSnoozes } from '@/utils/setupPromptSnooze'
import eventBus from '@/utils/eventBus'

// The collapsed flag is per browser, not per user: every new sign-in starts with the sidebar open.
function expandSidebarForNewSession() {
  try {
    localStorage.setItem('sidebarCollapsed', 'false')
  } catch {
    /* storage unavailable */
  }
  eventBus.emit('sidebar-state-changed', false)
}

const store = createStore({
  state: {
    user: null,
    isAuthenticated: false,
    authCheckInProgress: false,  // Track if auth check is in progress
    authInitPromise: null,       // Single promise for one-time session restore (guard waits for this)
    /** From GET /auth/public-site-config/ plus env fallbacks (wa.me URLs). */
    publicSiteConfig: null
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
      state.isAuthenticated = !!user
    },
    CLEAR_USER(state) {
      state.user = null
      state.isAuthenticated = false
    },
    SET_AUTH_CHECK_IN_PROGRESS(state, inProgress) {
      state.authCheckInProgress = inProgress
    },
    SET_PUBLIC_SITE_CONFIG(state, payload) {
      state.publicSiteConfig = payload && typeof payload === 'object' ? payload : null
    },
    RESET_AUTH_INIT(state) {
      state.authInitPromise = null
      state.authCheckInProgress = false
    }
  },
  actions: {
    async login({ commit }, credentials) {
      try {
        const response = await api.post('/auth/login/', credentials)
        const data = response.data
        
        // Store session key scoped to current tenant Host
        if (data && data.session_key) {
          setHostSessionKey(data.session_key)
        }
        
        // Save CSRF token from response (saved to both localStorage and sessionStorage)
        if (data && data.csrfToken) {
          saveCsrfToken(data.csrfToken)
        }
        
        // Remove internal fields from user data before storing
        const { session_key, csrfToken, ...userData } = data
        clearSetupPromptSnoozes()
        expandSidebarForNewSession()
        commit('SET_USER', userData)
        commit('RESET_AUTH_INIT')

        return userData
      } catch (error) {
        throw error
      }
    },
    async loginWithAssistToken({ commit }, token) {
      const response = await api.post('/auth/assist-login/', { token })
      const data = response.data
      if (data && data.session_key) {
        setHostSessionKey(data.session_key)
      }
      if (data && data.csrfToken) {
        saveCsrfToken(data.csrfToken)
      }
      const { session_key, csrfToken, ...userData } = data
      clearSetupPromptSnoozes()
      expandSidebarForNewSession()
      commit('SET_USER', userData)
      commit('RESET_AUTH_INIT')
      return userData
    },
    async register({ commit }, userData) {
      try {
        const response = await api.post('/auth/register/', userData)
        const data = response.data
        expandSidebarForNewSession()
        // Store session key scoped to current tenant Host
        if (data && data.session_key) {
          setHostSessionKey(data.session_key)
          const { session_key, ...userDataWithoutKey } = data
          commit('SET_USER', userDataWithoutKey)
          return userDataWithoutKey
        } else {
          commit('SET_USER', data)
          return data
        }
      } catch (error) {
        throw error
      }
    },
    async logout({ commit }) {
      clearSetupPromptSnoozes()
      try {
        await api.post('/auth/logout/', {}, {
          headers: { 'X-Session-Key': getHostSessionKey() }
        })
        clearHostSessionKey()
        clearHostCsrfToken()
        commit('CLEAR_USER')
        commit('RESET_AUTH_INIT')
      } catch (error) {
        clearHostSessionKey()
        clearHostCsrfToken()
        commit('CLEAR_USER')
        commit('RESET_AUTH_INIT')
      }
    },
    async fetchCurrentUser({ commit, state }) {
      // If already checking, wait for it to complete
      if (state.authCheckInProgress) {
        // Wait for the in-progress check to complete
        return new Promise((resolve) => {
          const checkInterval = setInterval(() => {
            if (!state.authCheckInProgress) {
              clearInterval(checkInterval)
              // Return the current user state after check completes
              resolve(state.user)
            }
          }, 50)
          // Timeout after 3 seconds
          setTimeout(() => {
            clearInterval(checkInterval)
            resolve(null)
          }, 3000)
        })
      }
      
      commit('SET_AUTH_CHECK_IN_PROGRESS', true)
      try {
        const response = await api.get('/auth/user/', {
          headers: { 'X-Session-Key': getHostSessionKey() }
        })
        if (response.data && !response.data.detail) {
          commit('SET_USER', response.data)
          commit('SET_AUTH_CHECK_IN_PROGRESS', false)
          return response.data
        } else {
          commit('CLEAR_USER')
          commit('SET_AUTH_CHECK_IN_PROGRESS', false)
          return null
        }
      } catch (error) {
        // If 401, user is not authenticated - this is fine, don't throw
        if (error.status === 401) {
          clearHostSessionKey()
          commit('CLEAR_USER')
          commit('SET_AUTH_CHECK_IN_PROGRESS', false)
          return null
        }
        clearHostSessionKey()
        commit('CLEAR_USER')
        commit('SET_AUTH_CHECK_IN_PROGRESS', false)
        return null
      }
    },
    /** Run once: restore session from session_key so direct URL loads see auth. Guard awaits this. */
    async ensureAuthInit({ state, dispatch }) {
      if (state.authInitPromise) return state.authInitPromise
      const run = async () => {
        await dispatch('fetchPublicSiteConfig')
        // Cookie-only production sessions have no localStorage key until /auth/user/ runs.
        // Always ensure CSRF cookie/token for current host (fixes "CSRF cookie not set" on staging/production)
        try {
          const r = await fetch(`${API_URL}/auth/csrf-token/`, { credentials: 'include' })
          if (r.ok) {
            const d = await r.json()
            if (d && d.csrfToken) saveCsrfToken(d.csrfToken)
          }
        } catch (_) {}
        await dispatch('fetchCurrentUser')
      }
      state.authInitPromise = run()
      return state.authInitPromise
    },
    async fetchPublicSiteConfig({ commit }) {
      try {
        const response = await api.get('/auth/public-site-config/')
        commit('SET_PUBLIC_SITE_CONFIG', response.data || {})
      } catch (_) {
        commit('SET_PUBLIC_SITE_CONFIG', {})
      }
    }
  },
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    currentUser: state => state.user,
    /** Primary shopper WhatsApp deep link (marketing hero, nav “Start Shopping”, demo cards). From GET /auth/public-site-config/ (WHATSAPP_PUBLIC_BASE_URL on Django). */
    whatsappEntryUrl(state) {
      const fromApi = (state.publicSiteConfig && state.publicSiteConfig.whatsapp_entry_url) || ''
      if (fromApi) return fromApi
      return resolvePublicWhatsappHref()
    },
    /** Support widget / optional separate number; falls back to whatsappEntryUrl then env. */
    supportWhatsappUrl(state, getters) {
      const fromApi = (state.publicSiteConfig && state.publicSiteConfig.support_whatsapp_url) || ''
      if (fromApi) return fromApi
      const envSupport = buildWaMeUrl(process.env.VUE_APP_SUPPORT_WHATSAPP_URL)
      if (envSupport) return envSupport
      return getters.whatsappEntryUrl || ''
    }
  }
})

export default store



